import { useMemo, useState } from "react";
import { Box, Button, Dialog, DialogActions, DialogContent, DialogProps, DialogTitle, Grid } from "@mui/material";
import { createEntityStore, UseEntityStore } from "../../common/entityStore";
import { propSimpleEnumField, propTextField } from "../../common/entityStoreForm";
import { renderClose, SimpleDialog } from "../../common/dialog";
import { ProductDto, TmCustomerSendSmsDto, TmOrderDto } from "./common";
import { getInvalidValueText, propIf, propLength, propNotRegex, propRequired, propValueValidator } from "../../common/entityStoreValidators";
import { useClientLanguages } from "../../common/enum";
import { EntityStoreSaveButton } from "../../common/SaveButton";
import { useRxJsSub } from "../../common/utils";
import { BehaviorSubject, combineLatest, distinctUntilChanged, map } from "rxjs";
import shallow from "zustand/shallow";
import { getResource } from "../../common/localization";
import { useConfig } from "../../common/config";
import { getPhoneFieldValidators, isMobile } from "../customer/validators";
import { keyBy } from "lodash";

function getText(key: string, defaultMessage?: string) {
    return getResource("Ui.Tm." + key, defaultMessage) as string;
}

function getCustomerText(key: string, defaultMessage?: string) {
    return getResource("Ui.Customer." + key, defaultMessage) as string;
}

export function SendSmsDialog(props: {
    dialogProps: DialogProps;
    close: () => void;
    useStore: UseEntityStore<TmOrderDto>;
    product$: BehaviorSubject<ProductDto | undefined>;
    simpleDialog: SimpleDialog;
}) {
    const [useStore] = useState(() => createEntityStore<TmCustomerSendSmsDto>({
        path: "tm/customers/sendSms",
        data: {
            custId: props.useStore.getState().clientData.custId,
            phone: props.useStore.getState().clientData.mob,
            language: props.useStore.getState().clientData.langPref,
            code: "",
            template: "",
            text: "",
        },
        id: null,
        async onSaved(p) {
            props.simpleDialog.show(getText("SendSms.MessageSent.Title"), getText("SendSms.MessageSent.Content"));
            props.close();
        },
    }));

    useRxJsSub(() => combineLatest([
        props.product$.pipe(map(p => p?.tmSmsTemplates)),
        useStore.getState().clientData$.pipe(map(p => ({ language: p.data.language, code: p.data.code })), distinctUntilChanged(shallow)),
        useClientLanguages.getState().items$.pipe(map(p => p.items)),
    ]).subscribe(([tmSmsTemplates, form, clientLanguages]) => {
        if (tmSmsTemplates == null) {
            useStore.getState().setClientDataProp("text", "");
            return;
        }

        let language = clientLanguages.length === 1 ? clientLanguages[0].id as string : form.language;
        let template = tmSmsTemplates.find(p => p.language === language);
        if (template == null) {
            template = tmSmsTemplates.at(0);
            language = template ? template.language : language;
        }

        if (language !== useStore.getState().clientData.language) {
            useStore.getState().setClientDataProp("language", language);
        }
        useStore.getState().setClientDataProp("template", template == null ? "" : template.text);
        useStore.getState().setClientDataProp("text", template == null ? "" : template.text.replaceAll("{code}", form.code));
    }));

    useRxJsSub(() => combineLatest([
        useClientLanguages.getState().items$.pipe(map(p => p.items)),
        useConfig.getState().clientData$.pipe(map(p => p.data.landCode)),
    ]).subscribe(([clientLanguages, landCode]) => {
        useStore.getState().setPropValidators([
            propRequired("phone"),
            clientLanguages.length > 1 && propRequired("language"),
            propIf(p => p.template.includes("{code}"), () => propRequired("code")),
            propLength("code", 8, 8),
            propNotRegex("code", /^[0-9a-zA-Z]+$/, getText("SendSms.CodeInvalid")),
            ...getPhoneFieldValidators(landCode, "phone"),
            propValueValidator("phone", p => typeof p === "string" && p && !isMobile(p, landCode) && getInvalidValueText()),
        ]);
    }));

    const templateLanguages = useMemo(() => keyBy(props.product$.getValue()?.tmSmsTemplates ?? [], t => t.language), [props.product$]);
    const allLanguages = useClientLanguages(p => p.items);
    const availableLanguages = allLanguages.filter(l => templateLanguages[l.id] != null);
    const { hasCode } = useStore(p => ({ hasCode: p.clientData.template.includes("{code}") }), shallow);

    return <Dialog {...props.dialogProps} maxWidth="sm"><Box m={2}>
        <DialogTitle>{getText("SendSms")}</DialogTitle>
        {renderClose(props.close)}
        <DialogContent>
            <Grid container columnSpacing={2}>
                <Grid item xs={4}>{propTextField(useStore)("phone", getCustomerText("Mob"))}</Grid>
                {hasCode ? <Grid item xs={4}>{propTextField(useStore)("code", getText("SendSms.Code"))}</Grid> : <Grid item xs={4} />}
                {allLanguages.length > 1 && <Grid item xs={4}>{propSimpleEnumField(useStore)("language", getCustomerText("Language"), availableLanguages)}</Grid>}
                <Grid item xs={12}>{propTextField(useStore)("text", getText("SendSms.Text"), { multiline: true, readOnly: true })}</Grid>
            </Grid>
        </DialogContent>
        <DialogActions>
            <EntityStoreSaveButton useStore={useStore} disableEnter text={getText("SendSms.Send")} />
            <Button onClick={props.close} color="negative" variant="outlined">{getResource("Ui.Close", "Close")}</Button>
        </DialogActions>
    </Box></Dialog>;
}
